import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Plus, Trash2, Save, Image as ImageIcon, Layers, Building2, Camera, Sliders } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";
import { ROOF_TYPE_TONE, ROOF_TYPES, tLabel } from "@/lib/roofTypes";
import { ProposalSystemsEditor, newSystem, systemFromSaved, systemToPayload } from "@/components/ProposalSystemsEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const assetSrc = (u) => (u && u.startsWith("/api/") ? BACKEND + u : u);
const SECTION_LABELS = { about_us: "About Us", testimonials: "Testimonials", awards: "Awards & Certifications", sales_sheet: "Manufacturer Sales Sheet", color_catalog: "Color Catalog", warranty: "Warranty" };
const DEFAULT_SECTIONS = { about_us: true, testimonials: true, awards: true, sales_sheet: true, color_catalog: true, warranty: true };
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

export function CustomizeProposalModal({ proposal, templates, onClose, onSaved }) {
  const { branding } = useBranding();
  const [scope, setScope] = useState(proposal.scope_items || []);
  const [upgrades, setUpgrades] = useState((proposal.upgrades || []).map((u) => ({ ...u, price: u.price_cents ? (u.price_cents / 100).toString() : "" })));
  const [tid, setTid] = useState("");
  const [saving, setSaving] = useState(false);
  const [manufacturer, setManufacturer] = useState(proposal.manufacturer || "");
  const [sections, setSections] = useState({ ...DEFAULT_SECTIONS, ...(proposal.sections || {}) });
  const [before, setBefore] = useState(proposal.before_photos || []);
  const [after, setAfter] = useState(proposal.after_photos || []);
  const [selectedTier, setSelectedTier] = useState(proposal.selected_tier || "SHINGLE");
  const [coverChoice, setCoverChoice] = useState(proposal.cover_choice || "auto");
  const [systems, setSystems] = useState((proposal.systems || []).map(systemFromSaved));
  const [defaultSq, setDefaultSq] = useState(null);
  useEffect(() => {
    if (!proposal.estimate_id) return;
    api.get(`/estimates/${proposal.estimate_id}`).then((r) => { if (r.data.squares) setDefaultSq(Math.round(r.data.squares * 1.15 * 10) / 10); }).catch(() => {});
  }, [proposal.estimate_id]);
  const multi = systems.length > 0;
  const catalog = branding.upgrade_catalog || [];
  const firstSystem = () => newSystem({
    roof_type: ROOF_TYPES.includes(proposal.selected_tier) ? proposal.selected_tier : "SHINGLE",
    scope_items: templates.length ? [] : scope.filter((s) => s.title).map((s) => ({ ...s })),
    before_photos: before.filter((b) => b.url), after_photos: after.filter((a) => a.url),
  });
  const addFromCatalog = (i) => {
    const c = catalog[Number(i)];
    if (c) setUpgrades((a) => [...a, { title: c.title, description: c.description || "", image_url: c.image_url || "", price: c.price_cents ? String(c.price_cents / 100) : "" }]);
  };
  const [tiers, setTiers] = useState(
    (proposal.tiers || []).map((t) => ({
      tier: t.tier,
      product: t.product || "",
      price: t.price_cents ? (t.price_cents / 100).toString() : "",
      inclusions: (t.inclusions || []).join("\n"),
    }))
  );

  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/proposal-asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const applyTemplate = (id) => {
    setTid(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setScope((t.items || []).map((i) => ({ ...i }))); toast.success(`Loaded "${t.name}"`); }
  };
  const setScopeItem = (i, k, v) => setScope((a) => a.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const rmScope = (i) => setScope((a) => a.filter((_, idx) => idx !== i));
  const addScope = () => setScope((a) => [...a, { title: "", description: "", image_url: "" }]);

  const setUp = (i, k, v) => setUpgrades((a) => a.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const rmUp = (i) => setUpgrades((a) => a.filter((_, idx) => idx !== i));
  const addUp = () => setUpgrades((a) => [...a, { title: "", description: "", image_url: "", price: "" }]);

  const setTierField = (i, k, v) => setTiers((a) => a.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));

  const save = async () => {
    setSaving(true);
    try {
      const tierPayload = tiers.map((t) => ({
        tier: t.tier,
        product: t.product,
        price_cents: Math.round(Number(t.price || 0) * 100),
        inclusions: t.inclusions.split("\n").map((s) => s.trim()).filter(Boolean),
      }));
      await api.put(`/proposals/${proposal.id}/scope`, {
        scope_items: scope.filter((s) => s.title).map((s) => ({ title: s.title, description: s.description || "", image_url: s.image_url || "" })),
        upgrades: upgrades.filter((u) => u.title).map((u) => ({ id: u.id, title: u.title, description: u.description || "", image_url: u.image_url || "", price_cents: Math.round(Number(u.price || 0) * 100) })),
        manufacturer, sections,
        before_photos: before.filter((b) => b.url),
        after_photos: after.filter((a) => a.url),
        tiers: tierPayload.length ? tierPayload : undefined,
        systems: systems.map(systemToPayload),
        selected_tier: selectedTier,
        cover_choice: coverChoice,
      });
      toast.success("Proposal customized"); onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="mb-card p-6 w-full max-w-4xl animate-scale-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customize-modal">
        <div className="flex justify-between items-center mb-4"><h3 className="font-display text-2xl font-bold uppercase text-navy">Customize Proposal</h3><button onClick={onClose} data-testid="customize-close"><X className="w-5 h-5 text-slate-400" /></button></div>

        <ProposalSystemsEditor systems={systems} setSystems={setSystems} templates={templates} library={branding.photo_library || []} onFirstAdd={firstSystem} defaultSquares={defaultSq} />

        {/* Legacy single roof-type editor (hidden once roof systems are used) */}
        {!multi && tiers.length > 0 && (
          <div className="border-2 border-slate-100 rounded-xl p-3 mb-5" data-testid="tier-editor">
            <div className="font-semibold text-navy text-sm mb-2 flex items-center gap-1"><Sliders className="w-4 h-4" /> Roof Type Options</div>
            <div className="text-[11px] uppercase text-slate-400 mb-2">Set the customer's chosen roof type, edit each option's product, price & what's included.</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {tiers.map((t, i) => (
                <div key={t.tier} className={`rounded-xl border-2 p-3 ${selectedTier === t.tier ? "border-navy bg-navy/5" : "border-slate-200"}`} data-testid={`tier-card-${t.tier}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`inline-block ${ROOF_TYPE_TONE[t.tier]} text-white text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full`}>{tLabel(t.tier)}</span>
                    <button onClick={() => setSelectedTier(t.tier)} data-testid={`select-tier-${t.tier}`}
                      className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${selectedTier === t.tier ? "bg-navy text-white border-navy" : "text-navy border-slate-300"}`}>
                      {selectedTier === t.tier ? "Selected" : "Select"}
                    </button>
                  </div>
                  <input data-testid={`tier-product-${t.tier}`} value={t.product} onChange={(e) => setTierField(i, "product", e.target.value)} placeholder="Product / material" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-navy mb-1.5" />
                  <div className="flex items-center gap-1 mb-1.5">
                    <span className="text-slate-400 text-sm">$</span>
                    <input data-testid={`tier-price-${t.tier}`} type="number" value={t.price} onChange={(e) => setTierField(i, "price", e.target.value)} placeholder="Price" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1 text-sm font-num outline-none focus:border-navy" />
                  </div>
                  <textarea data-testid={`tier-inclusions-${t.tier}`} rows={4} value={t.inclusions} onChange={(e) => setTierField(i, "inclusions", e.target.value)} placeholder="What's included&#10;(one item per line)" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-navy" />
                </div>
              ))}
            </div>
            {/* Comparison table */}
            <div className="overflow-x-auto" data-testid="tier-comparison">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 text-slate-400 font-semibold uppercase">Compare</th>
                    {tiers.map((t) => <th key={t.tier} className="text-left p-2"><span className={`inline-block ${ROOF_TYPE_TONE[t.tier]} text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full`}>{tLabel(t.tier)}</span></th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100">
                    <td className="p-2 text-slate-500 font-semibold">Product</td>
                    {tiers.map((t) => <td key={t.tier} className="p-2 text-navy">{t.product || "—"}</td>)}
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="p-2 text-slate-500 font-semibold">Price</td>
                    {tiers.map((t) => <td key={t.tier} className="p-2 font-num font-bold text-navy">{usd(Math.round(Number(t.price || 0) * 100))}</td>)}
                  </tr>
                  <tr className="border-t border-slate-100 align-top">
                    <td className="p-2 text-slate-500 font-semibold">Included</td>
                    {tiers.map((t) => {
                      const items = t.inclusions.split("\n").map((s) => s.trim()).filter(Boolean);
                      return <td key={t.tier} className="p-2 text-slate-600">{items.length ? <ul className="list-disc pl-4 space-y-0.5">{items.map((x, k) => <li key={k}>{x}</li>)}</ul> : "—"}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Cover image picker */}
        <div className="border-2 border-slate-100 rounded-xl p-3 mb-5" data-testid="cover-picker">
          <div className="font-semibold text-navy text-sm mb-2 flex items-center gap-1"><ImageIcon className="w-4 h-4" /> Proposal Cover</div>
          <div className="text-[11px] uppercase text-slate-400 mb-2">Choose the hero image on the proposal cover (PDF & online).</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["auto", "Auto (best available)"], ["aerial", "Aerial / Satellite"], ["street", "Street View"], ["ai", "AI Re-roof Preview"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`cover-${v}`} onClick={() => setCoverChoice(v)}
                className={`text-left rounded-xl border-2 px-3 py-2 text-xs font-semibold transition-colors ${coverChoice === v ? "border-navy bg-navy/5 text-navy" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-2">
            {coverChoice === "street" && "Uses Google Street View of the home's address (needs a Google Maps key in Branding)."}
            {coverChoice === "aerial" && "Uses a satellite/aerial view of the home's address."}
            {coverChoice === "ai" && "Uses the saved AI re-roof \"after\" image (create one in the Design tool)."}
            {coverChoice === "auto" && "Picks Street View, else aerial, else the AI re-roof image."}
          </div>
        </div>

        {!multi && <>
        <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Load a Scope Template</label>
        <select data-testid="apply-template" value={tid} onChange={(e) => applyTemplate(e.target.value)} className="mt-1 mb-4 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">
          <option value="">— pick a template to load its pictured steps —</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.items?.length || 0} steps)</option>)}
        </select>
        </>}

        <div className="border-2 border-slate-100 rounded-xl p-3 mb-5">
          <div className="font-semibold text-navy text-sm mb-2 flex items-center gap-1"><Building2 className="w-4 h-4" /> Presentation</div>
          <label className="text-xs font-semibold uppercase text-slate-500">Manufacturer</label>
          <select data-testid="builder-manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} className="mt-1 mb-3 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">
            <option value="">— none —</option>
            {(branding.manufacturers || []).map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
          </select>
          <div className="text-xs font-semibold uppercase text-slate-500 mb-1.5">Include sections (add / subtract paperwork)</div>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.keys(SECTION_LABELS).map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm text-navy">
                <input type="checkbox" data-testid={`section-${k}`} checked={!!sections[k]} onChange={(e) => setSections((s) => ({ ...s, [k]: e.target.checked }))} /> {SECTION_LABELS[k]}
              </label>
            ))}
          </div>
        </div>

        {!multi && <>
        <div className="font-semibold text-navy text-sm mb-2">Scope of Work ({scope.length})</div>
        <div className="space-y-2 mb-3">
          {scope.map((it, i) => (
            <div key={i} className="flex gap-2 items-start border-2 border-slate-100 rounded-xl p-2" data-testid={`scope-row-${i}`}>
              <div className="w-12 h-12 rounded border-2 border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">{it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}</div>
              <div className="flex-1 space-y-1">
                <input data-testid={`scope-title-${i}`} value={it.title} onChange={(e) => setScopeItem(i, "title", e.target.value)} placeholder="Step title" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-navy" />
                <input value={it.description} onChange={(e) => setScopeItem(i, "description", e.target.value)} placeholder="Description" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-navy" />
                <input type="file" accept="image/*" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 3 * 1024 * 1024) return toast.error("Under 3 MB"); setScopeItem(i, "image_url", await readFile(file)); }} className="text-[11px]" />
              </div>
              <button onClick={() => rmScope(i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <button data-testid="add-scope" onClick={addScope} className="text-xs text-navy font-semibold flex items-center gap-1 mb-5"><Plus className="w-3.5 h-3.5" /> Add scope step</button>
        </>}

        <div className="font-semibold text-navy text-sm mb-2">Optional Upgrades ({upgrades.length})</div>
        <div className="space-y-2 mb-3">
          {upgrades.map((u, i) => (
            <div key={i} className="flex gap-2 items-start border-2 border-slate-100 rounded-xl p-2" data-testid={`upgrade-row-${i}`}>
              <div className="w-12 h-12 rounded border-2 border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">{u.image_url ? <img src={u.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}</div>
              <div className="flex-1 space-y-1">
                <input data-testid={`up-title-${i}`} value={u.title} onChange={(e) => setUp(i, "title", e.target.value)} placeholder="Upgrade name (e.g. Solar Attic Vents)" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-navy" />
                <input value={u.description} onChange={(e) => setUp(i, "description", e.target.value)} placeholder="Description" className="w-full border-2 border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-navy" />
                <div className="flex items-center gap-2">
                  <input data-testid={`up-price-${i}`} type="number" value={u.price} onChange={(e) => setUp(i, "price", e.target.value)} placeholder="Price $" className="w-28 border-2 border-slate-200 rounded-lg px-2 py-1 text-sm font-num outline-none focus:border-navy" />
                  <input type="file" accept="image/*" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 3 * 1024 * 1024) return toast.error("Under 3 MB"); setUp(i, "image_url", await readFile(file)); }} className="text-[11px]" />
                </div>
              </div>
              <button onClick={() => rmUp(i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          {catalog.length > 0 && (
            <select data-testid="upgrade-catalog-select" value="" onChange={(e) => addFromCatalog(e.target.value)} className="border-2 border-slate-200 rounded-xl px-3 py-1.5 text-sm outline-none focus:border-navy">
              <option value="">+ Add from upgrade list…</option>
              {catalog.map((c, i) => <option key={i} value={i}>{c.title}</option>)}
            </select>
          )}
          <button data-testid="add-upgrade" onClick={addUp} className="text-xs text-navy font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add custom upgrade</button>
        </div>

        {(!multi || before.length > 0 || after.length > 0) && <>
        {multi && <div className="text-[11px] text-slate-400 mb-2">Proposal-wide photos (each roof system above also has its own before/after photos).</div>}

        <div className="font-semibold text-navy text-sm mb-2 flex items-center gap-1"><Camera className="w-4 h-4" /> Before Photos ({before.length})</div>
        <div className="space-y-2 mb-2">
          {before.map((ph, i) => (
            <div key={i} className="flex gap-2 items-center border-2 border-slate-100 rounded-xl p-2" data-testid={`before-${i}`}>
              <img src={assetSrc(ph.url)} alt="" className="w-12 h-12 rounded object-cover border border-slate-200 shrink-0" />
              <input value={ph.caption || ""} onChange={(e) => setBefore((a) => a.map((x, idx) => idx === i ? { ...x, caption: e.target.value } : x))} placeholder="Explain the problem (e.g. missing shingles, exposed felt)" className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-navy" />
              <button onClick={() => setBefore((a) => a.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <label className="text-xs text-navy font-semibold flex items-center gap-1 mb-5 cursor-pointer w-fit">
          <input type="file" accept="image/*" data-testid="add-before-photo" className="hidden" onChange={async (e) => { const ff = e.target.files?.[0]; if (!ff) return; if (ff.size > 25 * 1024 * 1024) return toast.error("Under 25 MB"); try { const d = await uploadPhoto(ff); setBefore((a) => [...a, { url: d.url, caption: "" }]); } catch { toast.error("Upload failed"); } e.target.value = ""; }} />
          <Plus className="w-3.5 h-3.5" /> Add before photo
        </label>
        {(branding.photo_library || []).length > 0 && (
          <div className="mb-5" data-testid="before-library">
            <div className="text-[11px] uppercase text-slate-400 mb-1">Or pick from your photo library</div>
            <div className="flex flex-wrap gap-2">
              {(branding.photo_library || []).map((ph, i) => (
                <button key={i} data-testid={`lib-before-${i}`} onClick={() => setBefore((a) => [...a, { url: ph.url, caption: ph.caption || "" }])} title="Add to before" className="w-14 h-14 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-navy"><img src={assetSrc(ph.url)} alt="" className="w-full h-full object-cover" /></button>
              ))}
            </div>
          </div>
        )}

        <div className="font-semibold text-navy text-sm mb-2 flex items-center gap-1"><Camera className="w-4 h-4" /> After Photos ({after.length})</div>
        <div className="flex flex-wrap gap-2 mb-2">
          {after.map((ph, i) => (
            <div key={i} className="relative" data-testid={`after-${i}`}>
              <img src={assetSrc(ph.url)} alt="" className="w-16 h-16 rounded object-cover border border-slate-200" />
              <button onClick={() => setAfter((a) => a.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full text-brandred border border-slate-200"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <label className="text-xs text-navy font-semibold flex items-center gap-1 mb-5 cursor-pointer w-fit">
          <input type="file" accept="image/*" data-testid="add-after-photo" className="hidden" onChange={async (e) => { const ff = e.target.files?.[0]; if (!ff) return; if (ff.size > 25 * 1024 * 1024) return toast.error("Under 25 MB"); try { const d = await uploadPhoto(ff); setAfter((a) => [...a, { url: d.url }]); } catch { toast.error("Upload failed"); } e.target.value = ""; }} />
          <Plus className="w-3.5 h-3.5" /> Add after photo
        </label>
        {(branding.photo_library || []).length > 0 && (
          <div className="mb-5" data-testid="after-library">
            <div className="text-[11px] uppercase text-slate-400 mb-1">Or pick from your photo library</div>
            <div className="flex flex-wrap gap-2">
              {(branding.photo_library || []).map((ph, i) => (
                <button key={i} data-testid={`lib-after-${i}`} onClick={() => setAfter((a) => [...a, { url: ph.url }])} title="Add to after" className="w-14 h-14 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-navy"><img src={assetSrc(ph.url)} alt="" className="w-full h-full object-cover" /></button>
              ))}
            </div>
          </div>
        )}

        </>}

        <button data-testid="save-customize" onClick={save} disabled={saving} className="w-full bg-navy text-white font-bold py-3 rounded-xl border-2 border-navy flex items-center justify-center gap-2 disabled:opacity-60"><Save className="w-4 h-4" /> Save Customization</button>
      </div>
    </div>
  );
}
